import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import toast from 'react-hot-toast'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Dropzone } from '@/components/ui/dropzone'
import { Image01Icon } from '@/components/ui/icons'
import { Spinner } from '@/components/ui/spinner'
import { PhotosStepGalleryItem } from '@/features/experience-shell/components/photos-step-gallery-item'
import { httpExperienceShellConfirmUploadPhoto } from '@/features/experience-shell/http/experience-shell.http.ts'
import { useBulkPresignedExperienceShellPhotos } from '@/features/experience-shell/queries/use-bulk-presigned-experience-shell-photos.ts'
import {
	type GalleryItem,
	useExperienceShellPhotoUpload
} from '@/features/service-vetting/store/services-photo-upload.ts'
import { useUploadPhotos } from '@/hooks/use-upload-photos'
import { QUERY_KEYS } from '@/query-client'
import { useTranslation } from '@/translations/i18n'
import type { FileToUpload } from '@/types/commons.ts'

const MAX_FILE_TO_UPLOAD = 28
export const MAXIMUM_IMAGES_ALLOWED = 30
export const PhotoStepGallery = ({
	experienceId,
	galleryId
}: {
	experienceId: number
	galleryId: number
}) => {
	const { t } = useTranslation()
	const [acceptedFiles, setAcceptedFiles] = useState<File[]>([])
	const setUploadingItemsStatus = useExperienceShellPhotoUpload(
		(store) => store.setUploadingItemsStatus
	)
	const addUploadProgressValues = useExperienceShellPhotoUpload(
		(store) => store.addUploadProgressValues
	)
	const addGalleryItems = useExperienceShellPhotoUpload(
		(store) => store.addGalleryItems
	)
	const galleryItems = useExperienceShellPhotoUpload(
		(store) => store.galleryItems
	)
	const galleryCounter = useExperienceShellPhotoUpload(
		(store) => store.galleryCounter
	)
	const setGalleryItemUrl = useExperienceShellPhotoUpload(
		(store) => store.setGalleryItemUrl
	)
	const queryClient = useQueryClient()

	const uploadPhotos = useUploadPhotos({
		storeAction: { setUploadingItemsStatus, addUploadProgressValues },
		onConfirm: async (imageId) => {
			const response = await httpExperienceShellConfirmUploadPhoto({
				experienceId,
				galleryId,
				mediaId: imageId
			})
			queryClient.invalidateQueries({
				queryKey: [QUERY_KEYS.EXPERIENCE_SHELL_GALLERY_IMAGES]
			})

			setGalleryItemUrl({ id: response.id, url: response.url })
			setUploadingItemsStatus({ id: response.id, status: 'uploaded' })
		}
	})
	const maxFilesAllowed = MAXIMUM_IMAGES_ALLOWED - galleryCounter
	const bulkPresignedQuery = useBulkPresignedExperienceShellPhotos({
		urlParams: { experienceId, galleryId },
		options: {
			onSuccess: (data) => {
				const galleryItems: GalleryItem[] = data.map((item, index) => ({
					id: item.media.id,
					url: URL.createObjectURL(acceptedFiles[index]),
					status: 'pending',
					name: item.media.name,
					thumbnails: null
				}))
				const uploadingItems: FileToUpload[] = data.map(
					(item, index) => ({
						id: item.media.id,
						presignedUrl: item.presignedUrl,
						file: acceptedFiles[index]
					})
				)
				addGalleryItems(galleryItems)
				uploadPhotos(uploadingItems)
			}
		}
	})

	const dropzoneState = useDropzone({
		disabled: bulkPresignedQuery.isPending,
		accept: {
			'image/jpeg': ['.jpeg'],
			'image/jpg': ['.jpg'],
			'image/png': ['.png']
		},
		maxFiles: MAX_FILE_TO_UPLOAD,
		multiple: true,
		onDropAccepted: (acceptedFiles) => {
			const filesToUpload = acceptedFiles.slice(0, maxFilesAllowed)
			if (filesToUpload.length > 0) {
				setAcceptedFiles(filesToUpload)
				bulkPresignedQuery.mutate({
					medias: filesToUpload.map((file) => file.name)
				})
			}
		},
		onDropRejected: (rejectedFiles) => {
			if (rejectedFiles.length > MAX_FILE_TO_UPLOAD) {
				return toast.error(
					t('errors:dropzone_max_file', { count: MAX_FILE_TO_UPLOAD })
				)
			}
			console.error('Rejected files: ', rejectedFiles)
		}
	})

	return (
		<>
			{galleryItems.length === 0 ? (
				<Dropzone
					className='h-[360px] w-full rounded-2xl border border-neutral-300 border-dashed'
					state={dropzoneState}
				>
					<div className='flex h-full w-full flex-col items-center justify-center gap-1 [&_svg]:text-neutral-600'>
						<div className='flex w-full flex-col items-center justify-center gap-4'>
							{bulkPresignedQuery.isPending ? (
								<Spinner size={32} />
							) : (
								<Image01Icon size={32} />
							)}
							<h3 className='font-medium text-base text-neutral-900'>
								{t('experience_shell:dropzone_title')}
							</h3>
						</div>
						<ButtonLegacy type='button' variant={'tertiary'}>
							{t('experience_shell:dropzone_description')}
						</ButtonLegacy>
					</div>
				</Dropzone>
			) : (
				<div className='flex w-full flex-col gap-6'>
					<div className='flex w-full items-center justify-between'>
						<p>
							{t('experience_shell:x_photos_uploaded', {
								count: galleryCounter
							})}
						</p>
						<Dropzone state={dropzoneState}>
							<ButtonLegacy
								disabled={
									bulkPresignedQuery.isPending ||
									galleryCounter >= MAXIMUM_IMAGES_ALLOWED
								}
								type='button'
								variant={'secondary'}
							>
								{t('common:upload')}
								{bulkPresignedQuery.isPending && (
									<Spinner size={32} />
								)}
							</ButtonLegacy>
						</Dropzone>
					</div>
					<div className='grid grid-cols-3 gap-5'>
						{galleryItems.map((item) => (
							<PhotosStepGalleryItem
								experienceId={experienceId}
								galleryId={galleryId}
								image={item}
								key={item.id}
							/>
						))}
					</div>
				</div>
			)}
		</>
	)
}
